import { useLayoutEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import SettingsIcon from "@mui/icons-material/Settings";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import TopBar from "./TopBar";
import SessionHeader from "./SessionHeader";
import ClearMachineCommandWatcher from "./ClearMachineCommandWatcher";
import QuarantineNotices from "./QuarantineNotices";
import AdminPinDialog from "./AdminPinDialog";
import { useScoreContext } from "../context/ScoreContext";
import { goToLockScreen, stashResumeRoute } from "../hooks/goToLockScreen";
import { getScribePin } from "../util/scribePin";
import { CORNER_BUTTON_SIZE, CORNER_BUTTON_GAP, CORNER_BUTTON_EDGE } from "../util/cornerButtons";

// Composes the persistent chrome (brand AppBar + Session strip) above
// the routed page content. Pages render their own breadcrumbs inside
// children since the crumb trail varies per screen.
const APP_BAR_HEIGHT = 64;

// #1665 — SessionHeader's own chip row can wrap to a second line whenever its
// content grows wide enough (position/judge/sequence chips can do this at
// narrow widths — #1701 makes judge/sequence collapse below `md` instead,
// but the mechanism here still has to hold for whatever wraps regardless).
// A sticky child below it (ScoringForm's "scoring-subheader",
// ClassList's sticky filter bar) can't know that height at author time, so it
// used to hardcode a single-line-assuming pixel offset (top:105 = AppBar 64 +
// SessionHeader py 16 + one chip row 24 + border 1) that silently went stale
// the moment SessionHeader grew a second row — exactly what the testName chip
// did, covering the CNO/Rider/Horse row (score-page-ui.cy.js, class-fault-
// time-limit.cy.js, both real regressions this card caused, proven via a
// call-site probe: neutering the testName fallback made both green again).
// Fixed at the root instead of re-guessing a new magic number: this measures
// SessionHeader's ACTUAL rendered height (ResizeObserver, so it tracks wrap
// changes live) and publishes it as a CSS custom property on the document
// root, with the old 105px as the var() fallback for the one frame before the
// effect runs. Consumers replace their hardcoded top with
// `var(--fv-session-header-bottom, 105px)`.
const SESSION_HEADER_BOTTOM_VAR = "--fv-session-header-bottom";

const CORNER_BUTTON_SX = {
  position: "fixed",
  bottom: CORNER_BUTTON_EDGE,
  zIndex: 1300,
  color: "text.secondary",
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  width: CORNER_BUTTON_SIZE,
  height: CORNER_BUTTON_SIZE,
  "&:hover": { bgcolor: "action.hover" },
};

export default function AppShell({
  children,
  sessionSubtitle = null,
  sessionNav = null,
  sessionJudge = null,
  sessionPosition = null,
  // #3379 -- the ride's ring, shown beside Position in the session strip.
  sessionRing = null,
  sessionSequence = null,
  // #636/#647 — the class currently in view, so SessionHeader can show its
  // next ride. null on screens with no single class in context (e.g. ClassList).
  classId = null,
  // #1557 — the classes screen has no single class, so it opts into a next-CLASS
  // chip instead of the next-ride one. See SessionHeader for the tri-state.
  showNextClass = false,
  nextClass = null,
  nextClassTime = null,
  nextClassTimeZone,
  // #2196 — passthrough for SessionHeader's next-class loading chip.
  nextClassLoading = false,
  // #3389 -- passthrough for SessionHeader's collapse (scoring screen only).
  sessionCollapsed = false,
  onToggleSessionCollapsed = null,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const location = useLocation();
  const { lockPicker, unlockPicker, selectedShow } = useScoreContext();
  const [gearOpen, setGearOpen] = useState(false);
  const sessionHeaderRef = useRef(null);

  // #3634 -- the padlock does exactly what the inactivity timeout does, through
  // the one shared helper (saves the resume route, locks, /setup with replace).
  const handleLock = () => {
    goToLockScreen({ navigate, currentPath: location.pathname, lockPicker });
  };

  // #3634 -- the gear asks only for the admin PIN. The right PIN saves this route
  // (Admin Options' Back returns here), unlocks the picker and lands on Admin
  // Options at /setup.
  const handleAdminUnlock = () => {
    setGearOpen(false);
    stashResumeRoute(location.pathname);
    unlockPicker();
    navigate("/setup");
  };

  // #1665 — see SESSION_HEADER_BOTTOM_VAR above. Runs on every render (not
  // just mount) since sessionNav/sessionJudge/etc. can change the chip
  // row's wrap without SessionHeader's own size changing in a way ResizeObserver
  // alone would catch fast enough for the very first paint after a route
  // change; the observer still covers later async-data-driven wraps (e.g. a
  // next-ride chip's label arriving after the initial mount).
  useLayoutEffect(() => {
    const el = sessionHeaderRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const publish = () => {
      const height = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        SESSION_HEADER_BOTTOM_VAR,
        `${APP_BAR_HEIGHT + height}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  });

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <TopBar />
      {/* #3634 -- two corner buttons: the gear (admin PIN) left of the padlock. */}
      <IconButton
        onClick={() => setGearOpen(true)}
        aria-label={t("adminOptions.gearAria")}
        data-testid="scribe-admin-gear"
        sx={{ ...CORNER_BUTTON_SX, right: CORNER_BUTTON_EDGE + CORNER_BUTTON_SIZE + CORNER_BUTTON_GAP }}
      >
        <SettingsIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <IconButton
        onClick={handleLock}
        aria-label={t("adminOptions.lockedPadlockAria")}
        data-testid="scribe-lock"
        sx={{ ...CORNER_BUTTON_SX, right: CORNER_BUTTON_EDGE }}
      >
        <LockIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <AdminPinDialog
        open={gearOpen}
        pin={getScribePin(selectedShow)}
        onUnlock={handleAdminUnlock}
        onCancel={() => setGearOpen(false)}
      />
      {/* #3518 -- the Station Monitor reset command poll (no UI). */}
      <ClearMachineCommandWatcher />
      {/* #3155 -- dismissible notice for a refused-and-quarantined write. */}
      <QuarantineNotices />
      <Box sx={{ pt: `${APP_BAR_HEIGHT}px` }}>
        <SessionHeader
          ref={sessionHeaderRef}
          subtitle={sessionSubtitle}
          nav={sessionNav}
          judge={sessionJudge}
          position={sessionPosition}
          ring={sessionRing}
          sequence={sessionSequence}
          classId={classId}
          showNextClass={showNextClass}
          nextClass={nextClass}
          nextClassTime={nextClassTime}
          nextClassTimeZone={nextClassTimeZone}
          nextClassLoading={nextClassLoading}
          collapsed={sessionCollapsed}
          onToggleCollapsed={onToggleSessionCollapsed}
        />
        <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1080, mx: "auto" }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
